/** stok — Waste records and the waste report. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokWaste = {
  // ---- Reasons and statuses -------------------------------------------------------------
  'stok.wasteReason.spoilage': 'Spoiled / rotten',
  'stok.wasteReason.expired': 'Expired',
  'stok.wasteReason.overproduction': 'Overproduction',
  'stok.wasteReason.preparation_loss': 'Preparation loss',
  'stok.wasteReason.contamination': 'Contamination',
  'stok.wasteReason.breakage': 'Broken / damaged',
  'stok.wasteReason.customer_return': 'Customer return',
  'stok.wasteReason.other': 'Other',
  'stok.wasteStatus.draft': 'Draft',
  'stok.wasteStatus.submitted': 'Awaiting approval',
  'stok.wasteStatus.approved': 'Approved',
  'stok.wasteStatus.posted': 'Posted',
  'stok.wasteStatus.rejected': 'Rejected',
  'stok.wasteStatus.cancelled': 'Cancelled',
  'stok.wasteStatus.reversed': 'Reversed',

  // ---- List -----------------------------------------------------------------------------
  'stok.waste.title': 'Waste',
  'stok.waste.subtitle':
    'Stock thrown away in {branch}: recorded, approved by someone else, then posted to the ledger.',
  'stok.waste.new': 'Record waste',
  'stok.waste.reportLink': 'Waste report',
  'stok.waste.listReadOnly': 'Your role can view waste records but not record waste.',
  'stok.waste.tabsLabel': 'Waste records by status',
  'stok.waste.tab.mine': 'Awaiting my approval',
  'stok.waste.tab.all': 'All',
  'stok.waste.listDesc': 'Newest first, up to 200 records. Value: posted value, or the estimate.',
  'stok.waste.searchPlaceholder': 'Number, item, SKU or lot',
  'stok.waste.mineDesc':
    'Submitted by someone else and waiting for your decision (separation of duties).',
  'stok.waste.empty': 'No waste recorded yet — use “Record waste” when stock is thrown away.',
  'stok.waste.emptyReadOnly': 'No waste recorded yet — kitchen and warehouse staff record it here.',
  'stok.waste.emptyMine': 'Nothing is waiting for your approval.',
  'stok.waste.emptyStatus': 'No waste records with this status.',
  'stok.waste.col.number': 'Number',
  'stok.waste.col.createdBy': 'Recorded by',
  'stok.waste.postedOn': 'Business date (posted)',
  'stok.waste.estimated': 'Estimate',
  'stok.waste.back': 'All waste',

  // ---- Form -----------------------------------------------------------------------------
  'stok.waste.newTitle': 'Record waste',
  'stok.waste.newSubtitle':
    'One item per record, at one location in {branch}. Stock changes only when the record is posted.',
  'stok.waste.readOnly': 'Your role can view waste but not record it.',
  'stok.waste.noPermission': 'Recording waste needs the waste permission. Ask a manager.',
  'stok.waste.noItems': 'No active inventory items yet — a manager creates items first.',
  'stok.waste.noLocations': 'No active location',
  'stok.waste.formTitle': 'What was wasted',
  'stok.waste.editTitle': 'Edit draft',
  'stok.waste.formDesc':
    'Quantity in any unit of the item. Lot-tracked items need the lot that was thrown away.',
  'stok.waste.pickItem': 'Choose an item…',
  'stok.waste.pickLot': 'Choose a lot…',
  'stok.waste.lotHint': 'Earliest expiry first. Choose the lot that was actually thrown away.',
  'stok.waste.lotLoading': 'Loading lots at this location…',
  'stok.waste.lotNone': 'No lot of this item has stock at this location.',
  'stok.waste.lotAvailable': 'In stock in this lot here: {qty}.',
  'stok.waste.lotExpiry': 'exp. {date} ({days})',
  'stok.waste.qty': 'Quantity',
  'stok.waste.unit': 'Unit',
  'stok.waste.inBase': 'Equals',
  'stok.waste.reason': 'Reason',
  'stok.waste.pickReason': 'Choose a reason…',
  'stok.waste.note': 'Note',
  'stok.waste.noteHint': 'What happened, e.g. “Fridge 2 failed overnight”.',
  'stok.waste.draftNote': 'Saved as a draft. Add photos and submit it on the next screen.',
  'stok.waste.saveDraft': 'Save draft',
  'stok.waste.saved': 'Waste {number} saved.',
  'stok.waste.err.location': 'Choose a location.',
  'stok.waste.err.item': 'Choose an item.',
  'stok.waste.err.lot': 'Choose the lot that was wasted.',
  'stok.waste.err.unit': 'Choose a unit for this item.',
  'stok.waste.err.qty': 'Enter a quantity above 0 (at most 3 decimals).',
  'stok.waste.err.precision': 'This quantity cannot be converted exactly — use a smaller unit.',
  'stok.waste.err.reason': 'Choose a reason.',
  'stok.waste.err.note': 'Use at most 500 characters.',
  'stok.waste.err.noteRequired': 'Write a note for this decision.',
  'stok.waste.err.selfApproval':
    'You recorded or submitted this waste, so you cannot decide on it. Another person with approval permission must approve or reject it.',
  'stok.waste.err.insufficient': 'Not enough stock at this location for this waste.',
  'stok.waste.err.insufficientQty':
    'Not enough stock at this location: {available} available, {requested} requested. Count the stock or change the quantity.',
  'stok.waste.err.stateChanged':
    'This record was changed by someone else. The latest state has been loaded.',

  // ---- Detail ---------------------------------------------------------------------------
  'stok.waste.detailLoading': 'Waste record',
  'stok.waste.detailTitle': 'Waste {number}',
  'stok.waste.detailSubtitle': '{item} · {location}',
  'stok.waste.factsTitle': 'Details',
  'stok.waste.factsDesc': 'Quantity as recorded, and in the item’s stock unit.',
  'stok.waste.liveEstimate': 'Estimated value (live)',
  'stok.waste.liveEstimateHint':
    'Quantity × current average cost. Frozen when the record is submitted.',
  'stok.waste.frozenEstimate': 'Estimated value',
  'stok.waste.frozenEstimateHint': 'Frozen at submission; decides whether approval is needed.',
  'stok.waste.noEstimate': 'No stock to value',
  'stok.waste.approvalRequired': 'Approval needed',
  'stok.waste.approvalYes': 'Yes — at or above the branch threshold',
  'stok.waste.approvalNo': 'No — below the branch threshold',
  'stok.waste.approvalUnknown': 'Decided at submission',
  'stok.waste.postedValue': 'Posted value',
  'stok.waste.postedValueHint': 'From the stock ledger at weighted average cost.',
  'stok.waste.businessDate': 'Business date',
  'stok.waste.movements': 'Ledger movements',
  'stok.waste.reversalMovement': 'reversal {id}',
  'stok.waste.actionsTitle': 'Next step',
  'stok.waste.actionsReadOnly': 'Your role cannot take the next step on this record.',
  'stok.waste.info.draft':
    'Draft — stock is not changed yet. Submitting freezes the estimated value and sends it for approval when needed.',
  'stok.waste.info.awaiting': 'Waiting for approval by someone with the approval permission.',
  'stok.waste.info.selfApproval':
    'You recorded this waste. Separation of duties: another person with approval permission must approve or reject it.',
  'stok.waste.info.decide': 'Check the quantity, reason and photos, then approve or reject.',
  'stok.waste.info.decideOwn':
    'You recorded this waste. Separation of duties is off in this branch, so you may decide — give a reason; it is logged as a self-approval.',
  'stok.waste.selfDecisionNote': 'Reason (required when approving your own record)',
  'stok.waste.info.approved': 'Approved — post it to remove the stock from the ledger.',
  'stok.waste.info.posted':
    'Posted to the stock ledger. Mistakes are corrected by a reversal, never by editing.',
  'stok.waste.info.rejected': 'Rejected — no stock was changed.',
  'stok.waste.info.reversed': 'Reversed — the stock and its value were added back.',
  'stok.waste.info.cancelled': 'Cancelled — no stock was changed.',
  'stok.waste.submit': 'Submit',
  'stok.waste.submitting': 'Submitting…',
  'stok.waste.submitted': 'Submitted for approval.',
  'stok.waste.submittedAuto': 'Submitted and approved automatically (below the threshold).',
  'stok.waste.approve': 'Approve',
  'stok.waste.approving': 'Approving…',
  'stok.waste.approveTitle': 'Approve waste {number}?',
  'stok.waste.approveBody': '{qty} of {item}. After approval it can be posted.',
  'stok.waste.decisionNote': 'Note',
  'stok.waste.approvedMsg': 'Waste approved.',
  'stok.waste.reject': 'Reject',
  'stok.waste.rejecting': 'Rejecting…',
  'stok.waste.rejectTitle': 'Reject waste {number}?',
  'stok.waste.rejectBody': 'No stock is changed. The person who recorded it sees your reason.',
  'stok.waste.rejectNote': 'Reason for rejection',
  'stok.waste.rejectedMsg': 'Waste rejected.',
  'stok.waste.post': 'Post',
  'stok.waste.posting': 'Posting…',
  'stok.waste.postTitle': 'Post waste {number}?',
  'stok.waste.postBody':
    '{qty} of {item} will be removed from {location} at the current average cost.',
  'stok.waste.postWarning': 'Posted waste can only be corrected by a reversal.',
  'stok.waste.postedMsg': 'Waste posted to the stock ledger.',
  'stok.waste.reverse': 'Reverse',
  'stok.waste.reversing': 'Reversing…',
  'stok.waste.reverseTitle': 'Reverse waste {number}?',
  'stok.waste.reverseBody':
    '{qty} is added back to {location} with exactly the value that left. The original entry stays in the ledger.',
  'stok.waste.reverseReason': 'Reason for the reversal',
  'stok.waste.reversedMsg': 'Waste reversed.',
  'stok.waste.cancel': 'Cancel draft',
  'stok.waste.cancelling': 'Cancelling…',
  'stok.waste.cancelTitle': 'Cancel waste {number}?',
  'stok.waste.cancelBody': 'The draft is closed and can no longer be submitted. No stock changes.',
  'stok.waste.cancelledMsg': 'Waste draft cancelled.',
  'stok.waste.timelineTitle': 'History',
  'stok.waste.timelineDesc': 'Times in the branch time zone ({timeZone}).',
  'stok.waste.tl.created': 'Recorded',
  'stok.waste.tl.submitted': 'Submitted',
  'stok.waste.tl.approved': 'Approved',
  'stok.waste.tl.autoApproved': 'Approved automatically (below the approval threshold)',
  'stok.waste.tl.rejected': 'Rejected',
  'stok.waste.tl.posted': 'Posted',
  'stok.waste.tl.reversed': 'Reversed',
  'stok.waste.tl.cancelled': 'Cancelled',

  // ---- Evidence -------------------------------------------------------------------------
  'stok.waste.evidence.title': 'Photos and documents',
  'stok.waste.evidence.desc':
    'Evidence for the approver. Photos are reduced to 1600 px; PDF up to 5 MB.',
  'stok.waste.evidence.add': 'Add photo or PDF',
  'stok.waste.evidence.preparing': 'Preparing…',
  'stok.waste.evidence.uploading': 'Uploading…',
  'stok.waste.evidence.uploaded': 'Evidence added.',
  'stok.waste.evidence.empty': 'No photos yet — add a photo of what was thrown away.',
  'stok.waste.evidence.emptyReadOnly': 'No photos or documents attached.',
  'stok.waste.evidence.errType': 'Use a JPEG, PNG or WebP photo, or a PDF.',
  'stok.waste.evidence.errSize': 'The file is too large — the limit is 5 MB.',
  'stok.waste.evidence.errDecode': 'This photo could not be read. Try another photo.',
  'stok.waste.evidence.open': 'Open evidence {n} in a new tab',
  'stok.waste.evidence.photoAlt': 'Evidence photo {n} for waste {number}',
  'stok.waste.evidence.pdf': 'PDF {n}',
  'stok.waste.evidence.size': '{kb} KB',

  // ---- Report ---------------------------------------------------------------------------
  'stok.waste.report.title': 'Waste report',
  'stok.waste.report.subtitle': 'Cost of posted waste in {branch}, from the stock ledger.',
  'stok.waste.report.rangeTitle': 'Period',
  'stok.waste.report.rangeDesc':
    'Business dates in the branch time zone ({timeZone}), up to {max} days.',
  'stok.waste.report.rangeTooLong': 'Choose a period of at most 93 days.',
  'stok.waste.report.period': '{from} – {to}',
  'stok.waste.report.total': 'Waste cost',
  'stok.waste.report.totalDesc': 'Net of reversals, in {currency}.',
  'stok.waste.report.count': 'Records',
  'stok.waste.report.countDesc': 'Posted waste records in the period.',
  'stok.waste.report.empty':
    'No posted waste in this period — figures appear once waste records are posted.',
  'stok.waste.report.byReason': 'By reason',
  'stok.waste.report.byItem': 'By item',
  'stok.waste.report.byCategory': 'By category',
  'stok.waste.report.byLocation': 'By location',
  'stok.waste.report.byDay': 'By day',
  'stok.waste.report.bucketEmpty': 'No data.',
  'stok.waste.report.records': 'Records',
  'stok.waste.report.recordsDesc':
    'Each record with the ledger movements its value comes from. Open a record for details.',
  'stok.waste.report.def.scope': 'Scope',
  'stok.waste.report.def.value': 'Value',
  'stok.waste.report.def.drilldown': 'Drilldown',
} as const;
